/**
 * Converts minutes to hours with 2 decimal precision (matching original app)
 * 
 * @param timeInMinutes Amount of time in minutes
 * @returns Value in hours with max 2 decimal places
 */
export const toHours = (timeInMinutes: number): number => {
  return Math.round((timeInMinutes / 60) * 100) / 100;
};

/**
 * Repairs a `timezoneOffset` value read from a stored record so it follows the
 * project convention: HOURS west of UTC (e.g. NYC = 5, Tokyo = -9).
 *
 * Some legacy writers (RoundTimer, the original injury writer) stored
 * `getTimezoneOffset()` directly — i.e. MINUTES (NYC = 300). Reading those
 * back as hours would multiply the offset by 60 and shift the record by
 * ~12.5 days. This helper detects the minute-encoding by magnitude
 * (no real-world TZ exceeds ±14 hours) and converts it back to hours.
 *
 * @param raw The stored timezone offset, or undefined/null
 * @returns Offset in hours, or undefined if not present
 */
export const normalizeTimezoneOffset = (raw: number | undefined | null): number | undefined => {
  if (raw === undefined || raw === null || Number.isNaN(raw)) return undefined;
  // Legitimate offsets are within ±14 (Kiribati = -14, US Samoa = 11).
  // Anything larger was written as minutes by a buggy writer.
  if (Math.abs(raw) > 24) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[date-time] timezoneOffset=${raw} looks like minutes, normalizing to hours`);
    }
    return raw / 60;
  }
  return raw;
};

/**
 * Shifts a UTC timestamp so that local accessors (`getDate`, `getHours`, ...)
 * yield the calendar day/time the user saw at the moment they saved the record.
 *
 * @param timestamp The UTC timestamp in milliseconds
 * @param timezoneOffset The original-TZ offset in hours west of UTC (NYC = 5, Tokyo = -9)
 * @returns A Date whose local accessors read the original-TZ wall clock
 *
 * Worked example — NYC user trained at 21:00 EST (= 02:00 UTC next day,
 * stored offset = 5), viewer is in Tokyo (current offset = -540 minutes):
 *   offsetDifference = -540 - 300 = -840 min = -14 h
 *   adjusted = 02:00 UTC Wed - 14h = 12:00 UTC Tue
 *   adjusted.getHours() in Tokyo TZ → 21 ✓
 *   adjusted.getDate()  in Tokyo TZ → Tuesday ✓
 *
 * Note: JavaScript's `getTimezoneOffset()` is "minutes WEST of UTC" — POSITIVE
 * for timezones behind UTC (Americas), NEGATIVE for timezones ahead (Asia).
 * The stored `timezoneOffset` follows the same sign convention but in HOURS.
 */
export const getDateWithTimezone = (timestamp: number, timezoneOffset?: number): Date => {
  const date = new Date(timestamp);

  // Back-compat shim: legacy writers stored minutes; convert to hours on read.
  const normalized = normalizeTimezoneOffset(timezoneOffset);

  // If no timezone offset provided, return the date as-is (local timezone)
  if (normalized === undefined) {
    return date;
  }

  // Both offsets are "minutes west of UTC".
  const currentOffset = date.getTimezoneOffset();
  const storedOffsetMinutes = normalized * 60;

  // To make local accessors yield the ORIGINAL wall clock, shift the epoch by
  // (currentOffset - storedOffset). This was previously inverted; same-TZ
  // users had diff=0 (masking the bug) while cross-TZ users saw records
  // shifted by ~2× the offset difference in the wrong direction.
  const offsetDifference = currentOffset - storedOffsetMinutes;
  const adjustedTime = date.getTime() + (offsetDifference * 60 * 1000);

  return new Date(adjustedTime);
};

/**
 * Returns the offset (in HOURS west of UTC, matching the project convention)
 * for a given instant in a specific IANA timezone — e.g. for "America/New_York"
 * on a winter date returns 5, on a summer date 4 (DST-aware).
 *
 * Used by the Apple Health import path: when the native plugin surfaces
 * `HKMetadataKeyTimeZone` for a workout, this turns it into the stored
 * `timezoneOffset` so a workout recorded in another TZ keeps its original
 * calendar day even when imported from a different device.
 *
 * Throws `RangeError` if `ianaName` isn't a recognized IANA zone.
 */
export const getOffsetHoursForZone = (date: Date, ianaName: string): number => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaName,
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const map: Record<string, string> = {};
  parts.forEach(p => { if (p.type !== 'literal') map[p.type] = p.value; });
  // `Intl` may emit hour=24 at midnight; normalize to 0.
  const hour = map.hour === '24' ? 0 : parseInt(map.hour, 10);
  const asUtc = Date.UTC(
    parseInt(map.year, 10),
    parseInt(map.month, 10) - 1,
    parseInt(map.day, 10),
    hour,
    parseInt(map.minute, 10),
    parseInt(map.second, 10),
  );
  // (asUtc - date.getTime()) is negative for zones west of UTC; negate to
  // match the "hours west" convention used everywhere else in this codebase.
  return -((asUtc - date.getTime()) / 3600000);
};

/**
 * Formats a date with timezone awareness
 *
 * @param timestamp The UTC timestamp in milliseconds
 * @param timezoneOffset The timezone offset in hours (from the original save)
 * @param options Intl.DateTimeFormatOptions for formatting
 * @returns A formatted date string
 */
export const formatDateWithTimezone = (
  timestamp: number,
  timezoneOffset?: number,
  options?: Intl.DateTimeFormatOptions
): string => {
  const date = getDateWithTimezone(timestamp, timezoneOffset);
  return date.toLocaleDateString('en-US', options);
};

/**
 * Formats a timestamp to a human-readable relative time string
 * 
 * @param timestamp The timestamp in milliseconds
 * @returns A string like "just now", "5 minutes ago", "2 hours ago", etc.
 */
export const getRelativeTimeString = (timestamp: number): string => {
  const now = Date.now();
  const seconds = Math.floor((now - timestamp) / 1000);
  
  // Just now
  if (seconds < 60) {
    return 'just now';
  }
  
  // Minutes
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  
  // Hours
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  
  // Days
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  
  // Weeks
  const weeks = Math.floor(days / 7);
  if (weeks < 4) {
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }
  
  // Months
  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }
  
  // Years
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

/**
 * Alias for getRelativeTimeString for consistency with other components
 */
export const timeAgo = getRelativeTimeString;

/**
 * Formats a timestamp to a date string
 * 
 * @param timestamp The timestamp in milliseconds or a Date object
 * @param format The format to use (default: 'short', options: 'short', 'long', 'time', 'month-year', or custom format)
 * @returns A formatted date string
 */
export const formatDate = (
  timestamp: number | Date, 
  format: 'short' | 'long' | 'time' | 'month-year' | string = 'short'
): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  
  // Short format: MM/DD/YYYY
  if (format === 'short') {
    return date.toLocaleDateString();
  }
  
  // Long format: Month DD, YYYY
  if (format === 'long') {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  // Time format: h:mm a
  if (format === 'time') {
    return date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  // Month Year format: Month YYYY
  if (format === 'month-year') {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long'
    });
  }
  
  // Support for custom date formats like 'YYYY-MM-DD'
  if (typeof format === 'string' && !['short', 'long', 'time', 'month-year'].includes(format)) {
    return formatCustomDate(date, format);
  }
  
  return date.toLocaleString();
};

/**
 * Formats a date according to a custom format string
 * Supports: YYYY, YY, MMMM, MMM, MM, M, DD, D, HH, H, mm, m, ss, s
 * 
 * @param date The date to format
 * @param format The format string
 * @returns Formatted date string
 */
export const formatCustomDate = (date: Date, format: string): string => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'];
  
  const monthShortNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Replace tokens in a SINGLE pass: sequential .replace() calls re-scanned
  // earlier replacements' output, so month names containing later tokens got
  // corrupted ('MMM D' for March rendered "3ar 10" — the M in "Mar" was
  // consumed by /M/g). Alternation is ordered longest-first so e.g. YYYY
  // matches before YY.
  const tokenValues: Record<string, string> = {
    YYYY: year.toString(),
    YY: (year % 100).toString().padStart(2, '0'),
    MMMM: monthNames[month],
    MMM: monthShortNames[month],
    MM: (month + 1).toString().padStart(2, '0'),
    M: (month + 1).toString(),
    DD: day.toString().padStart(2, '0'),
    D: day.toString(),
    HH: hours.toString().padStart(2, '0'),
    H: hours.toString(),
    mm: minutes.toString().padStart(2, '0'),
    m: minutes.toString(),
    ss: seconds.toString().padStart(2, '0'),
    s: seconds.toString()
  };

  return format.replace(
    /YYYY|YY|MMMM|MMM|MM|M|DD|D|HH|H|mm|m|ss|s/g,
    (token) => tokenValues[token]
  );
};

/**
 * Formats a Date as a `YYYY-MM-DD` key using LOCAL accessors (not UTC).
 *
 * Use this whenever you bucket trainings/events by day. The common
 * `date.toISOString().split('T')[0]` pattern is UTC-based and pushes an
 * evening training in a US timezone onto the next calendar day.
 *
 * If the Date has already been TZ-shifted (e.g. by `getDateWithTimezone`),
 * this returns the original-TZ day. If not, it returns the viewer's local day.
 */
export const formatLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parses a `YYYY-MM-DD` key back into a Date at local midnight.
 *
 * Symmetric to `formatLocalDateKey`. Avoids `new Date('2026-06-28')` which
 * parses as UTC midnight, then displays as the previous day for viewers
 * west of UTC.
 */
export const parseLocalDateKey = (key: string): Date => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Formats a duration in minutes to a readable time string
 * 
 * @param minutes The duration in minutes
 * @returns A string like "1h 30m" or "45m"
 */
export const formatDuration = (minutes: number): string => {
  if (minutes < 1) {
    return '< 1m';
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.floor(minutes % 60);
  
  if (hours === 0) {
    return `${remainingMinutes}m`;
  }
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
};

/**
 * Gets the start and end timestamps for a specific time period
 * 
 * @param period The time period (week, month, year, all)
 * @returns An object with start and end timestamps
 */
export const getTimePeriodBounds = (period: 'week' | 'month' | 'year' | 'all'): { start: number; end: number } => {
  const now = new Date();
  const end = now.getTime();
  
  if (period === 'all') {
    return { start: 0, end };
  }
  
  if (period === 'week') {
    const start = new Date();
    start.setDate(now.getDate() - 7);
    return { start: start.getTime(), end };
  }
  
  if (period === 'month') {
    const start = new Date();
    start.setMonth(now.getMonth() - 1);
    return { start: start.getTime(), end };
  }
  
  if (period === 'year') {
    const start = new Date();
    start.setFullYear(now.getFullYear() - 1);
    return { start: start.getTime(), end };
  }
  
  return { start: 0, end };
};

/**
 * Gets the number of days between two dates
 */
export const getDaysBetween = (start: number, end: number): number => {
  const oneDayMs = 24 * 60 * 60 * 1000;
  return Math.floor((end - start) / oneDayMs);
};

/**
 * Checks if a date is today
 */
export const isToday = (timestamp: number): boolean => {
  const today = new Date();
  const date = new Date(timestamp);
  
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Checks if a date is yesterday
 */
export const isYesterday = (timestamp: number): boolean => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  
  const date = new Date(timestamp);
  
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

/**
 * Gets a formatted date/time label appropriate for the timestamp
 */
export const getFriendlyDateLabel = (timestamp: number): string => {
  if (isToday(timestamp)) {
    return `Today at ${formatDate(timestamp, 'time')}`;
  }
  
  if (isYesterday(timestamp)) {
    return `Yesterday at ${formatDate(timestamp, 'time')}`;
  }
  
  // Within the last week
  const now = Date.now();
  const days = getDaysBetween(timestamp, now);
  
  if (days < 7) {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      hour: 'numeric',
      minute: '2-digit'
    });
  }
  
  // Default to standard format
  return formatDate(timestamp, 'long');
};

// getFormattedType has been moved to training-helpers.ts

/**
 * Format time duration in minutes to hours and minutes in the specific format used in stats display
 */
export const formatTime = (minutes: number): string => {
  if (minutes === 0) return '0 min';

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0) return `${remainingMinutes} min`;
  if (remainingMinutes === 0) return `${hours} hr`;

  return `${hours} hr ${remainingMinutes} min`;
};

/**
 * Formats seconds to MM:SS format for workout timers
 */
export const formatSeconds = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Formats seconds into hours, minutes, and seconds
 */
export const secondsToHMS = (seconds: number): { hours: number; minutes: number; seconds: number } => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return { hours, minutes, seconds: remainingSeconds };
};

/**
 * Formats hours, minutes, and seconds into a readable time string
 */
export const HMStoTimeString = (hms: { hours: number; minutes: number; seconds: number }): string => {
  const { hours, minutes, seconds } = hms;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};